import {
  makeApiProvider,
  useApiQuery,
} from '@pflegenavi/shared-frontend/platform';
import { createContext } from 'react';
import type { IUserProfileApi } from './api';
import { UserProfileApi } from './api';
import { useAuthentication } from '@pflegenavi/frontend/authentication';
import type {
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { UserProfileControllerResultTypes } from '@pflegenavi/service-provider-shared/api-endpoints';
import type { UpdateUserProfileOptionalInfoDto } from '@pflegenavi/shared/api';

const ApiContext = createContext<IUserProfileApi | undefined>(undefined);
const { useApi: useUserProfileApi, ApiProvider: UserProfileApiProvider } =
  makeApiProvider({
    name: 'UserProfile',
    ApiContext,
    newApi: (tenantId, auth, apiUrl) =>
      new UserProfileApi(tenantId, auth, apiUrl),
  });

export {
  useUserProfileApi,
  UserProfileApiProvider,
  ApiContext as UserProfileApiContext,
};
export type { IUserProfileApi } from './api';

const USER_PROFILE_KEY = (id: string) => [`user-profile`, id];

export const useUserProfile = (
  options: Omit<
    UseQueryOptions<
      UserProfileControllerResultTypes['getUserProfile'],
      unknown
    >,
    'queryFn' | 'queryKey'
  >
): UseQueryResult<
  UserProfileControllerResultTypes['getUserProfile'],
  unknown
> => {
  const { user } = useAuthentication();

  return useApiQuery(
    // eslint-disable-next-line react-hooks/rules-of-hooks
    () => useUserProfileApi(options.enabled === false),
    USER_PROFILE_KEY(user?.userId ?? ''),
    (api) => {
      return api?.getUserProfile() as Promise<
        UserProfileControllerResultTypes['getUserProfile']
      >;
    },
    options
  );
};

export const useUpdateUserProfile = (): UseMutationResult<
  UpdateUserProfileOptionalInfoDto,
  unknown,
  UpdateUserProfileOptionalInfoDto
> => {
  const api = useUserProfileApi();
  const queryClient = useQueryClient();
  const { user } = useAuthentication();

  return useMutation<
    UpdateUserProfileOptionalInfoDto,
    unknown,
    UpdateUserProfileOptionalInfoDto
  >({
    mutationKey: ['update_user'],
    mutationFn: (data) =>
      api?.updateUser(data) as unknown as Promise<{
        id: string;
      }>,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: USER_PROFILE_KEY(user?.userId ?? ''),
      });
    },
  });
};
